import { EXPERIENCE_STATUS } from '../../models/ExperienceStatus'
import * as Yup from 'yup'
import { rangeSchema, validateCapacityCoherence } from '../../../../helpers/FormatInputs';

const validationSchemaForDraft = Yup.object({
    /* step1  */
    id: Yup.number(),
    experienceType: Yup.string().required("Please select an option"),
    /* step2  */
    host_type: Yup.string().required("Please select an option"),
    host_id: Yup.number().required("Select the host of the experience").min(1,"Select the host of the experience"),
    title: Yup.string().required("Experience name is required").max(100, "Max 100 characters"),
    description: Yup.string().max(100, "Max 100 characters"),
    itinerary: Yup.string().max(10000, "Max length exceeded"),
    what_is_included: Yup.string().max(10000, "Max length exceeded"),
    availability_type: Yup.string(),
    dates_data: Yup.array().of(
        Yup.object().shape({
            start: Yup.string().nullable().required("Start date is required"),
            end: Yup.string().nullable()
                .required("End date is required")
                .test("is-after-start", "End date must be after start date", function (value) {
                    const { start } = this.parent;
                    if (!start || !value) return false;

                    return new Date(value) > new Date(start);
                }),
        })
    ).notRequired(),
    duration_type: Yup.mixed().nullable().notRequired(),
    duration: Yup.mixed().nullable().notRequired(),
    price_per_guest_text: Yup.string().max(13,"Max $1,000,000,000.00"),
    price_type: Yup.number().notRequired(),
    max_guests_per_group: Yup.number().notRequired().nullable(),
    enhancements_data: Yup.array().of(
        Yup.object().shape({
            name: Yup.string().required("Service name is required").max(100, "Max 100 characters"),
            price: Yup.number().required("Service price is required").min(0,'Min value is 0').max(1000000000,"Max values $1,000,000,000.00"),
            price_text: Yup.string()
        })
    ),
    category_ids: Yup.array().of(
        Yup.string().required("Category is required").max(100, "Max 100 characters"),
    ).notRequired(),
    city: Yup.string().nullable().max(200,"Max length exceeded"),
    location_address: Yup.string().max(200,"Max length exceeded"),
    arrival_notes: Yup.string().max(10000,"Max length exceeded"),
    public: Yup.boolean(),
    allowed_guest_ids: Yup.array().of(
            Yup.string()
            .required("Invitee is required")
            .max(100, "Max 100 characters").notRequired()
        ),
    audience_type: Yup.number(),
    guest_capacity_adults: rangeSchema(false),
    guest_capacity_children: rangeSchema(false),
    guest_capacity_youth: rangeSchema(false),
    guest_capacity_infants: rangeSchema(false),
    // UAT-212 P1: overall totals are optional for drafts (no coherence check).
    total_min_guests: Yup.string().notRequired().nullable(true),
    total_max_guests: Yup.string().notRequired().nullable(true),
    important_information_guest: Yup.string().max(10000, "Max length exceeded"),
    ideal_audience: Yup.string().max(10000, "Max length exceeded"),
    host_presence: Yup.string().max(100, "Max 100 characters"),
    additional_team_member_ids: Yup.array()
        .of(
            Yup.string()
            .required("Team member is required")
            .max(100, "Max 100 characters")
        ).notRequired(),
    beneficiary_for_profit: Yup.boolean(),
    beneficiary_check: Yup.boolean().when('beneficiary_for_profit', {
        is: false,
        then: Yup.boolean().oneOf([true], "You must confirm the beneficiary is a non-profit"),
        otherwise: Yup.boolean().notRequired(),
    }),
    payout_method: Yup.number().notRequired().nullable(),
    imageCover: Yup.mixed()
        .notRequired()
        .nullable()
        .test('fileType','Only images allowed',
            (value) => {
                if (!value) return true; // 👈 si no hay valor, pasa
                if (value instanceof File) {
                    return ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(value.type)
                }
                return false
            })
        .test('fileSize', 'Max size is 5MB',
            (value) => {
                if (!value) return true // 👈 clave
                if (value instanceof File) {
                    return value.size <= 5 * 1024 * 1024
                }
                return true
            }),
    galleryImages: Yup.array()
        .notRequired()
        .nullable()
        .of(
            Yup.mixed()
            .test('fileType', 'Only images allowed',
                (file) => {
                    if (!file) return true; // 👈 permite vacío
                    if (file instanceof File) {
                        return file.type?.startsWith('image/');
                    }
                    if (typeof file === 'string') {
                        try {
                            new URL(file);
                            return true;
                        } catch {
                            return false;
                        }
                    }
                    if (file && typeof file === 'object' && 'image' in file) {
                        try {
                            new URL((file as any).image);
                            return true;
                        } catch {
                            return false;
                        }
                    }
                    return false;
                })
            .test('fileSize', 'Max size is 5MB',
                (file) => {
                    if (file instanceof File) {
                        return file.size <= 5 * 1024 * 1024;
                    }
                    return true;
                })
        ),
    optional_video_link: Yup.string().url("Enter a valid URL").max(200, "Max 200 characters"),
    /* step3  */
    confidentiality_type: Yup.number(),
    confidentiality_check: Yup.boolean(),
    policy_cancelation_check: Yup.boolean(),
});

const validationSchemaForPending = Yup.object({
    /* step1  */
    id: Yup.number(),
    experienceType: Yup.string().required("Please select an option"),
    /* step2  */
    host_type: Yup.string().required("Please select an option"),
    host_id: Yup.number().required("Select the host of the experience").min(1,"Select the host of the experience"),
    title: Yup.string().required("Experience name is required").max(100, "Max 100 characters"),
    description: Yup.string().required("Experience description is required").max(100, "Max 100 characters"),
    itinerary: Yup.string().required("Itinerary is required").max(10000, "Max length exceeded"),
    what_is_included: Yup.string().required("Includes field is required").max(10000, "Max length exceeded"),
    availability_type: Yup.string().required("Please select an option"),
    dates_data: Yup.array().of(
        Yup.object().shape({
            start: Yup.string().nullable().required("Start date is required"),
            end: Yup.string().nullable()
                .required("End date is required")
                .test("is-after-start", "End date must be after start date", function (value) {
                    const { start } = this.parent;
                    if (!start || !value) return false;

                    return new Date(value) > new Date(start);
                }),
        })
    ).when("availability_type", {
        is: "by_date",
        then: (schema) =>
        schema
            .min(1, "Add at least one date")
            .required("Dates are required"),
        otherwise: (schema) => schema.notRequired(),
    }),
    duration_type: Yup.mixed()
        .nullable()
        .when("availability_type", {
            is: "by_date",
            then: (schema) => schema.notRequired().nullable(),
            otherwise: (schema) =>
                schema.test("is-required", "Select a duration", (val) => val !== null && val !== undefined && val !== ""),
        }),
    duration: Yup.mixed()
        .nullable()
        .when("availability_type", {
            is: "by_date",
            then: (schema) => schema.notRequired().nullable(),
            otherwise: (schema) =>
                schema.test("is-valid-duration", "Enter a valid duration (1-31)", (val) => {
                    if (val === null || val === undefined || val === "") return false;
                    const num = Number(val);
                    return !isNaN(num) && num >= 1 && num <= 31;
                }),
        }),
    price_per_guest_text: Yup.string().required("Enter a number").max(13,"Max $1,000,000,000.00"),
    price_type: Yup.number().required("Price type is required"),
    max_guests_per_group: Yup.number().when("price_type", {
        is: 2,
        then: (schema) => schema.typeError("Enter a number").required("Max guests per group is required").min(1, "Must be at least 1"),
        otherwise: (schema) => schema.notRequired().nullable(),
    }),
    enhancements_data: Yup.array().of(
        Yup.object().shape({
            name: Yup.string().required("Service name is required").max(100, "Max 100 characters"),
            price: Yup.number().required("Service price is required").min(0,'Min value is 0').max(1000000000,"Max values $1,000,000,000.00"),
            price_text: Yup.string()
        })
    ),
    category_ids: Yup.array().of(
        Yup.string().required("Category is required").max(100, "Max 100 characters"),
    ).min(1, "At least one category is required"),
    city: Yup.string().nullable()/* .required("City is required") */.max(200,"Max length exceeded"),
    location_address: Yup.string().required("Location is required").max(200,"Max length exceeded"),
    arrival_notes: Yup.string().required("Arrival notes are required").max(10000,"Max length exceeded"),
    public: Yup.boolean(),
    allowed_guest_ids: Yup.array().of(
            Yup.string()
            .required("Invitee is required")
            .max(100, "Max 100 characters")
        ).when('public', {
            is: 'private',
            then: (schema) =>
                schema.min(1, "At least one invitee is required").required("At least one invitee is required"),
            otherwise: (schema) => 
                schema.notRequired().nullable(true).min(0,""),
        }),
    audience_type: Yup.number().required('Select an audience type'),
    guest_capacity_adults: rangeSchema(true),
    guest_capacity_children: Yup.string().when('audience_type', {
        is: 1,
        then: rangeSchema(true),     // requerido + formato "min-max"
        otherwise: rangeSchema(false), // no requerido cuando audience_type = 'adults'
    }),
    guest_capacity_youth: Yup.string().when('audience_type', {
        is: 1,
        then: rangeSchema(true),
        otherwise: rangeSchema(false),
    }),
    guest_capacity_infants: Yup.string().when('audience_type', {
        is: 1,
        then: rangeSchema(true),
        otherwise: rangeSchema(false),
    }),
    // UAT-212 P1: overall totals required and consistent with the per-age sums
    // (sum(mins) <= total_min <= total_max <= sum(maxes)).
    total_min_guests: Yup.string().trim().matches(/^\d+$/, 'Enter a number').required('Required'),
    total_max_guests: Yup.string().trim().matches(/^\d+$/, 'Enter a number').required('Required')
        .test('capacity-coherence', 'Guest totals are inconsistent with the per-age capacity', function () {
            const message = validateCapacityCoherence(this.parent as any)
            return message ? this.createError({ message }) : true
        }),
    important_information_guest: Yup.string().max(10000, "Max length exceeded"),
    ideal_audience: Yup.string().max(10000, "Max length exceeded"),
    host_presence: Yup.string().max(100, "Max 100 characters").required("Participation is required"),
    additional_team_member_ids: Yup.array()
        .of(
            Yup.string()
            .required("Team member is required")
            .max(100, "Max 100 characters")
        ),
    beneficiary_for_profit: Yup.boolean(),
    beneficiary_check: Yup.boolean().when('beneficiary_for_profit', {
        is: false,
        then: Yup.boolean().oneOf([true], "You must confirm the beneficiary is a non-profit"),
        otherwise: Yup.boolean().notRequired(),
    }),
    payout_method: Yup.number()
            .required("Beneficiary is required").nullable(),
    imageCover: Yup.mixed()
        .required("Cover image is required")
        .test("fileType", "Only images allowed", (value) => {
            if (!value) return false;
            return ["image/jpeg", "image/png", "image/jpg", "image/webp"].includes(value.type);
        })
        .test("fileSize", "Max size is 5MB", (value) => {
            if (!value) return false;
            return value.size <= 5 * 1024 * 1024;
        }),
    galleryImages: Yup.array()
        .of(
            Yup.mixed().test("fileType", "Only images allowed", (file) => {
                if (!file) return false;
                if (file instanceof File) {
                    return file.type?.startsWith("image/");
                }
                if (typeof file === "string") {
                    try {
                        new URL(file);
                        return true;
                    } catch {
                        return false;
                    }
                }
                if (file && typeof file === "object" && "image" in file) {
                    try {
                        new URL((file as any).image);
                        return true;
                    } catch {
                        return false;
                    }
                }
                return false;
            })
            .test("fileSize", "Max size is 5MB", (value) => {
                if (value instanceof File) {
                    return value.size <= 5 * 1024 * 1024;
                }
                return true;
            })
        )
        .min(2, "At least two image is required"),
    optional_video_link: Yup.string().url("Enter a valid URL").max(200, "Max 200 characters"),
    /* step3  */
    confidentiality_type: Yup.number().required("Please select an option"),
    confidentiality_check: Yup.boolean().oneOf([true], "Policy confirmation is required").required("Policy confirmation is required"),
    policy_cancelation_check: Yup.boolean().oneOf([true], "Cancellation policy confirmation is required").required("Cancellation policy confirmation is required"),
});

const validationSchemaForDraftEdit = Yup.object({
    /* step1  */
    id: Yup.number(),
    experienceType: Yup.string().required("Please select an option"),
    /* step2  */
    host_type: Yup.string().required("Please select an option"),
    host_id: Yup.number().required("Select the host of the experience").min(1,"Select the host of the experience"),
    title: Yup.string().required("Experience name is required").max(100, "Max 100 characters"),
    description: Yup.string().max(100, "Max 100 characters"),
    itinerary: Yup.string().max(10000, "Max length exceeded"),
    what_is_included: Yup.string().max(10000, "Max length exceeded"),
    availability_type: Yup.string(),
    dates_data: Yup.array().of(
        Yup.object().shape({
            start: Yup.string().nullable().required("Start date is required"),
            end: Yup.string().nullable()
                .required("End date is required")
                .test("is-after-start", "End date must be after start date", function (value) {
                    const { start } = this.parent;
                    if (!start || !value) return false;

                    return new Date(value) > new Date(start);
                }),
        })
    ).when("availability_type", {
        is: "by_date",
        then: (schema) => schema.notRequired(),
        otherwise: (schema) => schema.notRequired(),
    }),
    duration_type: Yup.mixed().nullable().notRequired(),
    duration: Yup.mixed().nullable().notRequired(),
    price_per_guest_text: Yup.string().max(13,"Max $1,000,000,000.00"),
    price_type: Yup.number().notRequired(),
    max_guests_per_group: Yup.number().notRequired().nullable(),
    enhancements_data: Yup.array().of(
        Yup.object().shape({
            name: Yup.string().required("Service name is required").max(100, "Max 100 characters"),
            price: Yup.number().required("Service price is required").min(0,'Min value is 0').max(1000000000,"Max values $1,000,000,000.00"),
            price_text: Yup.string()
        })
    ),
    category_ids: Yup.array().of(
        Yup.string().required("Category is required").max(100, "Max 100 characters"),
    ).notRequired(),
    city: Yup.string().nullable().max(200,"Max length exceeded"),
    location_address: Yup.string().max(200,"Max length exceeded"),
    arrival_notes: Yup.string().max(10000,"Max length exceeded"),
    public: Yup.boolean(),
    allowed_guest_ids: Yup.array().of(
            Yup.string()
            .required("Invitee is required")
            .max(100, "Max 100 characters").notRequired()
        ),
    audience_type: Yup.number(),
    guest_capacity_adults: rangeSchema(false),
    guest_capacity_children: rangeSchema(false),
    guest_capacity_youth: rangeSchema(false),
    guest_capacity_infants: rangeSchema(false),
    // UAT-212 P1: overall totals are optional for drafts (no coherence check).
    total_min_guests: Yup.string().notRequired().nullable(true),
    total_max_guests: Yup.string().notRequired().nullable(true),
    important_information_guest: Yup.string().max(10000, "Max length exceeded"),
    ideal_audience: Yup.string().max(10000, "Max length exceeded"),
    host_presence: Yup.string().max(100, "Max 100 characters"),
    additional_team_member_ids: Yup.array()
        .of(
            Yup.string()
            .required("Team member is required")
            .max(100, "Max 100 characters")
        ).notRequired(),
    beneficiary_for_profit: Yup.boolean(),
    beneficiary_check: Yup.boolean().when('beneficiary_for_profit', {
        is: false,
        then: Yup.boolean().oneOf([true], "You must confirm the beneficiary is a non-profit"),
        otherwise: Yup.boolean().notRequired(),
    }),
    payout_method: Yup.number().notRequired().nullable(),
    imageCover: Yup.mixed()
        .notRequired()
        .nullable()
        .test("fileOrUrl", "Only images or valid URLs allowed", (value) => {
            if (!value) return true;

            // Caso 1: File
            if (value instanceof File) {
                return ["image/jpeg", "image/png", "image/jpg", "image/webp"].includes(value.type);
            }

            // Caso 2: URL string
            if (typeof value === "string") {
                try {
                    const url = new URL(value);
                    return true; // si parsea bien, es URL válida
                } catch {
                    return false;
                }
            }

            return false;
        })
        .test("fileSize", "Max size is 5MB", (value) => {
            if (value instanceof File) {
                return value.size <= 5 * 1024 * 1024;
            }
            return true; // si es URL, no aplica límite de tamaño
        }),
    galleryImages: Yup.array()
        .notRequired()
        .nullable()
        .of(
            Yup.mixed()
                .test("fileOrUrl", "Only images or valid URLs allowed", (file) => {
                    if (!file) return true;

                    // Caso 1: File
                    if (file instanceof File) {
                        return file.type?.startsWith("image/");
                    }

                    // Caso 2: URL string
                    if (typeof file === "string") {
                        try {
                            const url = new URL(file);
                            return true;
                        } catch {
                            return false;
                        }
                    }

                    // Caso 3: Objeto { id: number, image: string }
                    if (file && typeof file === "object" && "image" in file) {
                        try {
                            const url = new URL((file as any).image);
                            return true;
                        } catch {
                            return false;
                        }
                    }

                    return false;
                })
                .test("fileSize", "Max size is 5MB", (value) => {
                    if (value instanceof File) {
                        return value.size <= 5 * 1024 * 1024;
                    }
                    return true; // si es URL, no aplica límite
                })
        ),
    optional_video_link: Yup.string().url("Enter a valid URL").max(200, "Max 200 characters"),
    /* step3  */
    confidentiality_type: Yup.number(),
    confidentiality_check: Yup.boolean(),
    policy_cancelation_check: Yup.boolean(),
});

const validationSchemaPendingEdit = Yup.object({
    /* step1  */
    id: Yup.number(),
    experienceType: Yup.string().required("Please select an option"),
    /* step2  */
    host_type: Yup.string().required("Please select an option"),
    host_id: Yup.number().required("Select the host of the experience").min(1,"Select the host of the experience"),
    title: Yup.string().required("Experience name is required").max(100, "Max 100 characters"),
    description: Yup.string().required("Experience description is required").max(100, "Max 100 characters"),
    itinerary: Yup.string().required("Itinerary is required").max(10000, "Max length exceeded"),
    what_is_included: Yup.string().required("Includes field is required").max(10000, "Max length exceeded"),
    availability_type: Yup.string().required("Please select an option"),
    dates_data: Yup.array().of(
        Yup.object().shape({
            start: Yup.string().nullable().required("Start date is required"),
            end: Yup.string().nullable()
                .required("End date is required")
                .test("is-after-start", "End date must be after start date", function (value) {
                    const { start } = this.parent;
                    if (!start || !value) return false;

                    return new Date(value) > new Date(start);
                }),
        })
    ).when("availability_type", {
        is: "by_date",
        then: (schema) =>
        schema
            .min(1, "Add at least one date")
            .required("Dates are required"),
        otherwise: (schema) => schema.notRequired(),
    }),
    duration_type: Yup.mixed()
        .nullable()
        .when("availability_type", {
            is: "by_date",
            then: (schema) => schema.notRequired().nullable(),
            otherwise: (schema) =>
                schema.test("is-required", "Select a duration", (val) => val !== null && val !== undefined && val !== ""),
        }),
    duration: Yup.mixed()
        .nullable()
        .when("availability_type", {
            is: "by_date",
            then: (schema) => schema.notRequired().nullable(),
            otherwise: (schema) =>
                schema.test("is-valid-duration", "Enter a valid duration (1-31)", (val) => {
                    if (val === null || val === undefined || val === "") return false;
                    const num = Number(val);
                    return !isNaN(num) && num >= 1 && num <= 31;
                }),
        }),
    price_per_guest_text: Yup.string().required("Enter a number").max(13, "Max $1,000,000,000.00"),
    price_type: Yup.number().required("Price type is required"),
    max_guests_per_group: Yup.number().when("price_type", {
        is: 2,
        then: (schema) => schema.typeError("Enter a number").required("Max guests per group is required").min(1, "Must be at least 1"),
        otherwise: (schema) => schema.notRequired().nullable(),
    }),
    enhancements_data: Yup.array().of(
        Yup.object().shape({
            name: Yup.string().required("Service name is required").max(100, "Max 100 characters"),
            price: Yup.number().required("Service price is required").min(0, 'Min value is 0').max(1000000000, "Max values $1,000,000,000.00"),
            price_text: Yup.string()
        })
    ),
    category_ids: Yup.array().of(
        Yup.string().required("Category is required").max(100, "Max 100 characters"),
    ).min(1, "At least one category is required"),
    city: Yup.string().nullable()/* .required("City is required") */.max(200,"Max length exceeded"),
    location_address: Yup.string().required("Location is required").max(200,"Max length exceeded"),
    arrival_notes: Yup.string().required("Arrival notes are required").max(10000,"Max length exceeded"),
    public: Yup.boolean(),
    allowed_guest_ids: Yup.array().of(
        Yup.string()
            .required("Invitee is required")
            .max(100, "Max 100 characters")
    ).when('public', {
        is: 'private',
        then: (schema) =>
            schema.min(1, "At least one invitee is required").required("At least one invitee is required"),
        otherwise: (schema) =>
            schema.notRequired().nullable(true).min(0, ""),
    }),
    audience_type: Yup.number().required('Select an audience type'),
    guest_capacity_adults: rangeSchema(true),
    guest_capacity_children: Yup.string().when('audience_type', {
        is: 1,
        then: rangeSchema(true),     // requerido + formato "min-max"
        otherwise: rangeSchema(false), // no requerido cuando audience_type = 'adults'
    }),
    guest_capacity_youth: Yup.string().when('audience_type', {
        is: 1,
        then: rangeSchema(true),
        otherwise: rangeSchema(false),
    }),
    guest_capacity_infants: Yup.string().when('audience_type', {
        is: 1,
        then: rangeSchema(true),
        otherwise: rangeSchema(false),
    }),
    // UAT-212 P1: overall totals required and consistent with the per-age sums
    // (sum(mins) <= total_min <= total_max <= sum(maxes)).
    total_min_guests: Yup.string().trim().matches(/^\d+$/, 'Enter a number').required('Required'),
    total_max_guests: Yup.string().trim().matches(/^\d+$/, 'Enter a number').required('Required')
        .test('capacity-coherence', 'Guest totals are inconsistent with the per-age capacity', function () {
            const message = validateCapacityCoherence(this.parent as any)
            return message ? this.createError({ message }) : true
        }),
    important_information_guest: Yup.string().max(10000, "Max length exceeded"),
    ideal_audience: Yup.string().max(10000, "Max length exceeded"),
    host_presence: Yup.string().max(100, "Max 100 characters").required("Participation is required"),
    additional_team_member_ids: Yup.array()
        .of(
            Yup.string()
                .required("Team member is required")
                .max(100, "Max 100 characters")
        ),
    beneficiary_for_profit: Yup.boolean(),
    beneficiary_check: Yup.boolean().when('beneficiary_for_profit', {
        is: false,
        then: Yup.boolean().oneOf([true], "You must confirm the beneficiary is a non-profit"),
        otherwise: Yup.boolean().notRequired(),
    }),
    payout_method: Yup.number().required("Beneficiary is required").nullable(),
    imageCover: Yup.mixed()
        .required("Cover image is required")
        .test("fileOrUrl", "Only images or valid URLs allowed", (value) => {
            if (!value) return false;

            // Caso 1: File
            if (value instanceof File) {
                return ["image/jpeg", "image/png", "image/jpg", "image/webp"].includes(value.type);
            }

            // Caso 2: URL string
            if (typeof value === "string") {
                try {
                    const url = new URL(value);
                    return true; // si parsea bien, es URL válida
                } catch {
                    return false;
                }
            }

            return false;
        })
        .test("fileSize", "Max size is 5MB", (value) => {
            if (value instanceof File) {
                return value.size <= 5 * 1024 * 1024;
            }
            return true; // si es URL, no aplica límite de tamaño
        }),
    galleryImages: Yup.array()
        .of(
            Yup.mixed()
                .test("fileOrUrl", "Only images or valid URLs allowed", (file) => {
                    if (!file) return false;

                    // Caso 1: File
                    if (file instanceof File) {
                        return file.type?.startsWith("image/");
                    }

                    // Caso 2: URL string
                    if (typeof file === "string") {
                        try {
                            const url = new URL(file);
                            return true;
                        } catch {
                            return false;
                        }
                    }

                    // Caso 3: Objeto { id: number, image: string }
                    if (file && typeof file === "object" && "image" in file) {
                        try {
                            const url = new URL((file as any).image);
                            return true;
                        } catch {
                            return false;
                        }
                    }

                    return false;
                })
                .test("fileSize", "Max size is 5MB", (value) => {
                    if (value instanceof File) {
                        return value.size <= 5 * 1024 * 1024;
                    }
                    return true; // si es URL, no aplica límite
                })
        )
        .min(2, "At least two images are required"),
    optional_video_link: Yup.string().url("Enter a valid URL").max(200, "Max 200 characters"),
    /* step3  */
    confidentiality_type: Yup.number().required("Please select an option"),
    confidentiality_check: Yup.boolean().oneOf([true], "Policy confirmation is required").required("Policy confirmation is required"),
    policy_cancelation_check: Yup.boolean().oneOf([true], "Cancellation policy confirmation is required").required("Cancellation policy confirmation is required"),
});

const validateShowButtonActions = (key: 'draft' | 'paused' | 'publish', formik:any) => {
    let show = '';
    switch (key) {
        case 'draft':
            if(formik.values?.id > 0){
                if([EXPERIENCE_STATUS.PUBLISHED, EXPERIENCE_STATUS.PRE_LAUNCH ].includes(formik.values?.status)){
                    show = 'd-none';
                }
            }
            break;
        case 'paused':
            if([EXPERIENCE_STATUS.PUBLISHED, EXPERIENCE_STATUS.PRE_LAUNCH ].includes(formik.values?.status)){
                show = 'd-none';
            }
            break;
        case 'publish':
            show = formik.values?.id > 0 ? '' : 'd-none'
            break;
        default:
            break;
    }
    return show
}

export { validateShowButtonActions, validationSchemaForDraft, validationSchemaForPending, validationSchemaForDraftEdit, validationSchemaPendingEdit };